import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetHandle,
  BottomSheetHandleProps,
  BottomSheetModal,
} from "@gorhom/bottom-sheet";
import {
  AHIcon,
  AHQueryHandlerLoading,
  AHText,
  Box,
  useLayoutContext,
} from "arkh";
import React, {
  ForwardedRef,
  Suspense,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { HeaderButton } from "../../../../components";
import { useWindowDimensions } from "react-native";

import { SelectTemporaryScreen } from "./SelectTemporaryScreen";
import { StartPrompt } from "./StartPrompt";
import { SetupScreen } from "./SetupScreen";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { UserLoggedInNavigatorParamList } from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useNavigation } from "@react-navigation/native";
import { useSizeClass } from "../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import {
  CONTENT_HEIGHT_FIRST_SNAP,
  FOOTER_HEIGHT,
  HANDLE_HEIGHT,
} from "./layout";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import {
  graphql,
  loadQuery,
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useRelayEnvironment,
} from "react-relay";
import { StorecastFocusBottomSheetQuery } from "./__generated__/StorecastFocusBottomSheetQuery.graphql";
import { StorecastFocusBottomSheetClearTemporaryScreeensMutation } from "./__generated__/StorecastFocusBottomSheetClearTemporaryScreeensMutation.graphql";
import { StorecastState } from "../types";
import { useFocusEnd } from "../useFocusEnd";

type Props = {
  dashboard: {
    id: string;
  };
  setStorecastState: (state: StorecastState) => void;
};

export const StorecastFocusBottomSheet = React.memo(
  React.forwardRef(StorecastFocusBottomSheetComponent)
);

export type StorecastFocusBottomSheetRef = {
  open: (storecastId: string) => void;
};

const QUERY = graphql`
  query StorecastFocusBottomSheetQuery($id: ID!) {
    storecast {
      screens {
        id
        name
        activeSession {
          id
        }
        dashboard {
          id
        }
      }
    }
    dashboard(id: $id) @required(action: THROW) {
      id
      name
      screens {
        id
        name
        activeSession {
          id
        }
      }
      temporaryScreen {
        id
      }
    }
  }
`;

function StorecastFocusBottomSheetComponent(
  props: Props,
  ref: ForwardedRef<StorecastFocusBottomSheetRef>
) {
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const endFocus = useFocusEnd(props.dashboard.id);
  const onClose = useCallback(() => {
    props.setStorecastState("IDLE");
    endFocus();
  }, [props, endFocus]);

  const snapPoints = useMemo(
    () => [
      HANDLE_HEIGHT +
        CONTENT_HEIGHT_FIRST_SNAP +
        FOOTER_HEIGHT +
        layout.space.large,
    ],
    [layout.space.large]
  );

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const currentQuery =
    useRef<PreloadedQuery<StorecastFocusBottomSheetQuery> | null>(null);

  const [clearTempScreens] =
    useMutation<StorecastFocusBottomSheetClearTemporaryScreeensMutation>(
      graphql`
        mutation StorecastFocusBottomSheetClearTemporaryScreeensMutation(
          $storecastId: ID!
        ) {
          dashboard(dashboardId: $storecastId) {
            clearTemporaryScreens
          }
        }
      `
    );
  const env = useRelayEnvironment();
  useImperativeHandle(ref, () => ({
    open(storecastId) {
      if (currentQuery.current) {
        currentQuery.current.dispose();
        currentQuery.current = null;
      }

      currentQuery.current = loadQuery<StorecastFocusBottomSheetQuery>(
        env,
        QUERY,
        {
          id: storecastId,
        },
        {
          fetchPolicy: "store-and-network",
        }
      );
      clearTempScreens({
        variables: {
          storecastId,
        },
      });

      bottomSheetRef.current?.present(currentQuery.current);
    },
  }));

  const onDismiss = useCallback(() => {
    if (currentQuery.current) {
      currentQuery.current.dispose();
      currentQuery.current = null;
    }
  }, []);

  const onStartFocusMode = useCallback(() => {
    bottomSheetRef.current?.dismiss();
  }, []);

  const onPressCancel = useCallback(() => {
    bottomSheetRef.current?.dismiss();
    onClose();
  }, [onClose]);
  const renderHandle = useCallback(
    (p: BottomSheetHandleProps) => {
      return (
        <BottomSheetHandle
          {...p}
          style={{
            paddingBottom: 0,
            paddingHorizontal: 0,
            height: HANDLE_HEIGHT,
          }}
        >
          <Box spaceHorizontal="large" style={{ alignItems: "flex-end" }}>
            <HeaderButton type="CANCEL" onPress={onPressCancel} />
          </Box>
          <Box style={{ alignItems: "center" }} spaceHorizontal="large">
            <AHIcon
              spaceRight="small"
              type="focus-mode"
              size="xlarge"
              color="darkerText"
            />
            <AHText type="title-big">{intl_tokens.FocusMode}</AHText>
          </Box>
        </BottomSheetHandle>
      );
    },
    [intl_tokens.FocusMode, onPressCancel]
  );

  const dimensions = useWindowDimensions();
  const width = dimensions.width;
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const bottomSheetWidth = 600;

  const margin = useMemo(() => {
    return (width - bottomSheetWidth) / 2;
  }, [width, bottomSheetWidth]);

  const renderContent = (args: {
    data?: PreloadedQuery<StorecastFocusBottomSheetQuery>;
  }) => {
    if (!args.data) {
      return <></>;
    }
    const data = args.data;
    return (
      <Suspense
        fallback={
          <Box
            style={{
              flex: 1,
              marginTop: -100,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AHQueryHandlerLoading />
          </Box>
        }
      >
        <BottomSheetModalContent
          query={data}
          onStartFocusMode={onStartFocusMode}
        />
      </Suspense>
    );
  };

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        pressBehavior="none"
        opacity={isCompact ? 0.5 : 0.2}
      />
    ),
    [isCompact]
  );

  return (
    <BottomSheetModal
      detached
      ref={bottomSheetRef}
      backdropComponent={renderBackdrop}
      onDismiss={onDismiss}
      backgroundStyle={{ borderRadius: 32, flex: 1 }}
      enablePanDownToClose={false}
      enableDismissOnClose
      activeOffsetY={100}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      handleComponent={renderHandle}
      bottomInset={layout.space.large}
      style={[
        {
          flex: 1,
          marginHorizontal: isCompact ? layout.space.large : margin,
        },
      ]}
    >
      {renderContent}
    </BottomSheetModal>
  );
}

function BottomSheetModalContent(props: {
  query: PreloadedQuery<StorecastFocusBottomSheetQuery>;
  onStartFocusMode: () => void;
}) {
  const data = usePreloadedQuery(QUERY, props.query);

  const navigation =
    useNavigation<NativeStackNavigationProp<UserLoggedInNavigatorParamList>>();

  const allScreens = useMemo(
    () =>
      [...data.dashboard.screens, ...data?.storecast.screens].filter(
        ({ id }, index, arr) =>
          index === arr.findIndex((item) => item.id === id)
      ),
    [data.dashboard.screens, data?.storecast.screens]
  );

  const screenIdsWithDashboardCurrentlyOn = useMemo(() => {
    const int = new Set<string>();

    data.storecast.screens.forEach((screen) => {
      if (screen.dashboard?.id === data.dashboard.id) {
        int.add(screen.id);
      }
    });
    data.dashboard.screens.forEach((screen) => {
      int.add(screen.id);
    });
    return int;
  }, [data.dashboard.id, data.dashboard.screens, data.storecast.screens]);

  const onlineStoreScreens = useMemo(
    () =>
      allScreens
        .filter((screen) => screen.activeSession)
        .slice()
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        }) ?? [],
    [allScreens]
  );

  const onlineNonSelectedScreens = useMemo(
    () =>
      onlineStoreScreens.filter(
        ({ id }) =>
          !screenIdsWithDashboardCurrentlyOn.has(id) &&
          id !== data?.dashboard?.temporaryScreen?.id
      ),
    [
      onlineStoreScreens,
      screenIdsWithDashboardCurrentlyOn,
      data?.dashboard?.temporaryScreen?.id,
    ]
  );

  const onlineStorecastScreens = useMemo(() => {
    return (
      onlineStoreScreens.filter(
        (screen) =>
          screenIdsWithDashboardCurrentlyOn.has(screen.id) ||
          screen.id === data?.dashboard?.temporaryScreen?.id
      ) ?? []
    );
  }, [
    onlineStoreScreens,
    screenIdsWithDashboardCurrentlyOn,
    data?.dashboard?.temporaryScreen?.id,
  ]);

  const [isSelectingTemporaryScreen, setIsSelectingTemporaryScreen] =
    useState(false);
  const state = useMemo(() => {
    if (isSelectingTemporaryScreen) {
      return "SELECT_TEMPORARY_SCREEN";
    }
    if (onlineStorecastScreens.length > 0) {
      return "START";
    }
    if (onlineNonSelectedScreens.length > 0) {
      return "SELECT_TEMPORARY_SCREEN";
    }
    return "SETUP_SCREEN";
  }, [
    isSelectingTemporaryScreen,
    onlineStorecastScreens.length,
    onlineNonSelectedScreens.length,
  ]);

  const storecastId = data.dashboard.id;
  const storecastName = data.dashboard.name;

  const onAddScreen = useCallback(() => {
    navigation.navigateDeprecated("StoreAdmin", {
      screen: "AddScreen",
      params: {
        fromStorecast: {
          id: storecastId,
          name: storecastName,
        },
      },
    });
  }, [navigation, storecastId, storecastName]);

  switch (state) {
    case "START":
      return (
        <StartPrompt
          screens={onlineStorecastScreens}
          hasOtherScreens={onlineNonSelectedScreens.length > 0}
          onAddScreen={onAddScreen}
          onChangeScreens={() => {
            setIsSelectingTemporaryScreen(true);
          }}
          onStartFocusMode={props.onStartFocusMode}
        />
      );
    case "SELECT_TEMPORARY_SCREEN":
      return (
        <SelectTemporaryScreen
          onAddScreen={onAddScreen}
          onlineNonSelectedScreens={onlineNonSelectedScreens}
          onStartFocusMode={props.onStartFocusMode}
          storecastId={storecastId}
        />
      );
    case "SETUP_SCREEN":
      return (
        <SetupScreen
          hasOfflineScreens={(data?.storecast.screens.length ?? 0) > 0}
          storecastId={storecastId}
          onAddScreen={onAddScreen}
        />
      );
  }
}
